import { FC, useEffect, useState } from 'react';

import { $isListItemNode, $isListNode } from '@lexical/list';
import { $findMatchingParent } from '@lexical/utils';
import {
  activeEditor$,
  editorInTable$,
  iconComponentFor$,
  SingleChoiceToggleGroup,
  type IconKey,
} from '@mdxeditor/editor';
import { useCellValue, useCellValues } from '@mdxeditor/gurx';
import {
  $getSelection,
  $isDecoratorNode,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  type ElementFormatType,
} from 'lexical';

import { isBlockAlignment, type TextAlignment } from './utils';

const ALIGNMENT_ITEMS: {
  value: TextAlignment;
  title: string;
  icon: IconKey;
}[] = [
  { value: 'left', title: 'Align left', icon: 'format_align_left' },
  { value: 'center', title: 'Align center', icon: 'format_align_center' },
  { value: 'right', title: 'Align right', icon: 'format_align_right' },
];

const getSelectedBlocksAlignment = (): TextAlignment | null => {
  const selection = $getSelection();

  if (!$isRangeSelection(selection)) {
    return 'left';
  }

  const nodes = selection.getNodes();
  const seenKeys = new Set<string>();
  const alignments = new Set<ElementFormatType>();

  for (const node of nodes) {
    const blockNode = $findMatchingParent(
      node,
      (n) => $isElementNode(n) && !n.isInline(),
    );

    if (!$isElementNode(blockNode)) continue;

    const key = blockNode.getKey();
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);

    alignments.add(blockNode.getFormatType());
  }

  if (alignments.size === 0) return 'left';

  if (alignments.size === 1) {
    const format = [...alignments][0];
    return isBlockAlignment(format) ? format : 'left';
  }

  return null;
};

const getIsAlignmentDisabled = (): boolean => {
  const selection = $getSelection();

  if ($isNodeSelection(selection)) {
    return selection.getNodes().some($isDecoratorNode);
  }

  if (!$isRangeSelection(selection)) return false;

  const seenBlockKeys = new Set<string>();

  for (const node of selection.getNodes()) {
    if ($isDecoratorNode(node)) return true;

    if ($findMatchingParent(node, (n) => $isListNode(n) || $isListItemNode(n)))
      return true;

    const blockParent = $findMatchingParent(
      node,
      (n) => $isElementNode(n) && !n.isInline(),
    );

    if ($isElementNode(blockParent)) {
      const key = blockParent.getKey();
      if (!seenBlockKeys.has(key)) {
        seenBlockKeys.add(key);
        if (blockParent.getChildren().some($isDecoratorNode)) return true;
      }
    }
  }

  return false;
};

export const TextAlignmentToggle: FC = () => {
  const [activeEditor, iconComponentFor] = useCellValues(
    activeEditor$,
    iconComponentFor$,
  );

  const isInTable = useCellValue(editorInTable$);
  const [alignment, setAlignment] = useState<TextAlignment | null>('left');
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    if (!activeEditor) {
      return;
    }

    activeEditor.getEditorState().read(() => {
      setAlignment(getSelectedBlocksAlignment());
      setIsDisabled(getIsAlignmentDisabled());
    });

    return activeEditor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        setAlignment(getSelectedBlocksAlignment());
        setIsDisabled(getIsAlignmentDisabled());
      });
    });
  }, [activeEditor]);

  const applyAlignment = (value: TextAlignment | '') => {
    if (!activeEditor) {
      return;
    }

    activeEditor.dispatchCommand(
      FORMAT_ELEMENT_COMMAND,
      (value === 'left' ? '' : value) as ElementFormatType,
    );
  };

  return (
    <SingleChoiceToggleGroup
      value={alignment ?? ''}
      disabled={isInTable || isDisabled}
      onChange={(value) => applyAlignment(value as TextAlignment | '')}
      items={ALIGNMENT_ITEMS.map(({ value, title, icon }) => ({
        value,
        title,
        contents: iconComponentFor(icon),
      }))}
    />
  );
};
